import { HexDistanceInfo, PathData, PathsToEnemyData, SearchSource, TacticalMapData, TacticalNode, ZocNode } from "@/core/battle/ai/types/ai.types";
import { PLANNING_CONFIG } from "@/core/battle/ai/utils/AIConfig";
import { PriorityQueue } from "@/core/battle/ai/utils/evaluatorUtils";
import { CombatCalculator } from "@/core/battle/CombatCalculator";
import ManagerRegistry from "@/core/battle/ManagerRegistry";
import { PathCalculator } from "@/core/battle/PathCalculator";
import { EFFECTIVE_COUNT, TERRAINS } from "@/core/constants/battle.constants";
import { BattleUnit, Hex } from "@/core/types/battle.types";
import { getUnitTotalHp } from "@/core/utils/battle.utils";
import { coordsToId, getAxialNeighbors, getHexDistance, idToCoords } from "@/core/utils/hex.utils";


export class PathsToEnemyCalculator {

    public static calculate(
        sources: SearchSource[],
        unit: BattleUnit,
        zocMap: Map<string, ZocNode>,
        battleMap: Map<string, Hex>,
        maxHexes?: number
    ): TacticalMapData {
        const registry = ManagerRegistry.getInstance().entityRegistry;

        const distMap = new Map<string, TacticalNode>();
        const queue = new PriorityQueue<string>();
        const sourcesById = new Map<string, SearchSource>();
        const sourceHexIds = new Set<string>();
        const stackHP = getUnitTotalHp(unit);

        for (const source of sources) {
            
            const startId = coordsToId(source);
            sourcesById.set(source.id, source);
            sourceHexIds.add(coordsToId(source));
            // Ініціалізуємо без directDist, він тут не потрібен для черги
            distMap.set(startId, { dist: 0, moveCost: 0, targetId: source.id });
            if (maxHexes && distMap.size >= maxHexes) break;
            queue.push(startId, 0);
        }

        while (!queue.isEmpty()) {
            if (maxHexes && distMap.size >= maxHexes) break;
            const item = queue.pop();
            if (!item) break;
            const { currentId, currentCost } = item;
            const currentNode = distMap.get(currentId)!;
            
            if (currentCost > currentNode.moveCost) continue;

            const { q, r } = registry.getHex(currentId)?.data ?? idToCoords(currentId);
            
            const isSource = sourceHexIds.has(currentId);
            const zocNode = isSource ? null : zocMap.get(currentId);
            const rawThreat = zocNode?.totalThreat || 0;
            const attackersCount = zocNode?.attackersCount || 0;
            const dangerFactor = Math.min(1.0, rawThreat / stackHP);

            for (const neighbor of getAxialNeighbors(q, r)) {
                const nId = coordsToId(neighbor);
                const neighborHex = battleMap.get(nId);
                
                if (!neighborHex || neighborHex.isObstacle || neighborHex.entityId && neighborHex.entityId !== unit.unitId) continue;

                const terrainCfg = TERRAINS[neighborHex.terrain];
                const baseCost = PathCalculator.getStepCost(unit.stats.speed, terrainCfg?.moveCost);

                let penalty = 0;
                if (attackersCount > 0) {
                    // Формула "липкості" та летальності ZoC
                    const stickyPenalty = baseCost * attackersCount;
                    const lethalPenalty = Math.pow(dangerFactor, 2) * (baseCost * 3);
                    penalty = stickyPenalty + lethalPenalty;
                }

                const newCumulativeDist = currentCost + baseCost + penalty;
                const existingNode = distMap.get(nId);

                if (!existingNode || newCumulativeDist < existingNode.moveCost) {
                    distMap.set(nId, {
                        dist: currentNode.dist + 1,
                        moveCost: newCumulativeDist,
                        targetId: currentNode.targetId, // Успадковуємо найближчу ціль
                        parentId: currentId
                    });
                    queue.push(nId, newCumulativeDist);
                }
            }
        }

        let minDist = Number.MAX_SAFE_INTEGER;
        let maxDist = 0;
        const targets: Record<string, { nearestHex: string; minMoveCost: number, minDist: number }> = {};
        const nodes: Record<string, TacticalNode> = {};

        // ФІНАЛЬНИЙ ПРОХІД: Розраховуємо directDist ТІЛЬКИ ТУТ
        for (const [hexId, node] of distMap) {
            if (node.dist === 0) continue; 

            // Оптимізація: рахуємо геометрію тільки для фінального вузла
            const target = sourcesById.get(node.targetId!);
            if (target) {
                const hexCoords: { q: number; r: number; } = registry.getHex(hexId)?.data ?? idToCoords(hexId);
                node.directDist = getHexDistance(target, hexCoords);
            }

            nodes[hexId] = node;
            minDist = Math.min(minDist, node.moveCost);
            maxDist = Math.max(maxDist, node.moveCost);

            // Оновлюємо дані про найближчі точки до кожного ворога
            if (node.targetId) {
                const existing = targets[node.targetId];
                // Використовуємо moveCost як критерій "найближчості" (логічніше для AI)
                if (!existing || node.moveCost < existing.minMoveCost) {
                    targets[node.targetId] = { 
                        nearestHex: hexId, 
                        minMoveCost: node.moveCost,
                        minDist: minDist
                    };
                }
            }
        }

        return {
            nodes,
            stats: { minDist, maxDist },
            targets
        };
    }


    public static reconstructPath(targetId: string, nodes: Map<string, any>): string[] {
        const path: string[] = [];
        let current: string | undefined = targetId;

        while (current) {
            path.unshift(current);
            current = nodes.get(current);
        }

        return path; // Повертає [startId, ..., targetId]
    } 
}